import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "../firebase"; 
import { 
    doc, getDoc, updateDoc, deleteDoc, writeBatch, Timestamp, collection, query,      
    where, orderBy, limit, getDocs 
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";

import { 
    Box, Typography, CircularProgress, Alert, Paper, Divider, Grid,      
    Button, TextField, Select, MenuItem, InputLabel, FormControl, Link, Chip,
    IconButton, Stack, Avatar, Dialog, DialogActions, DialogContent, 
    DialogContentText, DialogTitle
} from "@mui/material";
import { styled } from "@mui/system";

// Icone
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

// --- STYLED COMPONENTS ---
const MainContainer = styled(Box)({
  minHeight: "100vh",
  background: "#F8F9FA",
  padding: "30px",
  fontFamily: "'Inter', sans-serif",
});

const ModernCard = styled(Paper)({
  background: "#FFFFFF",
  borderRadius: "24px",
  boxShadow: "0px 10px 40px rgba(0,0,0,0.04)",
  padding: "24px",
  marginBottom: "24px",
  overflow: "hidden"
});

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "1.1rem",
  color: "#2d3436",
  marginBottom: "16px",
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
});

// Helpers Data
const formatTimestamp = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return 'N/D';
  return timestamp.toDate().toLocaleDateString('it-IT', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
};

const isExpired = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return true;
  const expiryDate = timestamp.toDate();
  expiryDate.setHours(23, 59, 59, 999);
  return expiryDate < new Date();
};

export default function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestCertificate, setLatestCertificate] = useState(null);

  // Stati Rinnovo
  const [isRenewalFormOpen, setIsRenewalFormOpen] = useState(false);
  const [renewalPeriod, setRenewalPeriod] = useState('Mensile');
  const [renewalPrice, setRenewalPrice] = useState(null); 
  const [carnetEntries, setCarnetEntries] = useState(10);
  const [isQuotaConfirmationOpen, setIsQuotaConfirmationOpen] = useState(false); 
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  
  // MODIFICA A: Nuovo stato esplicito per la data di fine
  const [endDate, setEndDate] = useState('');

  // Stato Eliminazione
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // --- LOGICHE CALCOLO DATE ---
  const calculateEndDate = (start, period) => {
    if (!start || !period || period === 'Personalizzato') return '';
    const date = new Date(start);
    switch (period) {
        case 'Mensile': date.setMonth(date.getMonth() + 1); break;
        case 'Trimestrale': date.setMonth(date.getMonth() + 3); break;
        case 'Annuale': date.setFullYear(date.getFullYear() + 1); break;
        case 'Carnet': date.setFullYear(date.getFullYear() + 1); break;
        default: return '';
    }
    return date.toISOString().split('T')[0];
  };

  // MODIFICA A: Aggiorna automaticamente la data di fine se selezioni un periodo standard, altrimenti lascia il controllo all'utente
  useEffect(() => {
    if (renewalPeriod !== 'Personalizzato') {
        const autoDate = calculateEndDate(startDate, renewalPeriod);
        if (autoDate) setEndDate(autoDate);
    }
  }, [startDate, renewalPeriod]);

  const checkOverlap = () => {
    if (!user || !user.abbonamentoScadenza || !startDate) return false;
    const currentExpiry = user.abbonamentoScadenza.toDate();
    currentExpiry.setHours(0, 0, 0, 0);
    const startSelected = new Date(startDate);
    startSelected.setHours(0, 0, 0, 0);
    return startSelected < currentExpiry;
  };

  const isOverlapping = checkOverlap();

  useEffect(() => {
    const fetchUserAndCertificate = async () => {
      setLoading(true);
      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          setError("Utente non trovato.");
          setLoading(false);
          return;
        }

        const userData = userDoc.data();
        setUser({ id: userDoc.id, ...userData });
        setRenewalPeriod(userData.tipoAbbonamento || 'Mensile'); 
        setRenewalPrice(userData.costoAbbonamento || null);
        
        const certificatesCol = collection(db, "userCertificates");
        const q = query(certificatesCol, where("userId", "==", userId), orderBy("uploadedAt", "desc"), limit(1));
        const certificateSnap = await getDocs(q);
        if (!certificateSnap.empty) {
            setLatestCertificate({ id: certificateSnap.docs[0].id, ...certificateSnap.docs[0].data() });
        }
      } catch (err) {
        console.error("Errore fetch:", err);
        setError("Errore nel caricamento dati.");
      } finally {
        setLoading(false);
      }
    };
    fetchUserAndCertificate();
  }, [userId]);

  const handleDeleteUser = async () => {
    setLoading(true);
    try {
        const batch = writeBatch(db);
        const certsQuery = query(collection(db, "userCertificates"), where("userId", "==", userId));
        const certsSnap = await getDocs(certsQuery);
        
        const deleteFilePromises = [];
        certsSnap.forEach((certDoc) => {
            const data = certDoc.data();
            if (data.fileName) {
                const fileRef = ref(storage, `certificates/${userId}/${data.fileName}`);
                deleteFilePromises.push(deleteObject(fileRef).catch(() => {}));
            }
            batch.delete(certDoc.ref);
        });

        await Promise.all(deleteFilePromises);
        batch.delete(doc(db, "users", userId));
        await batch.commit();
        navigate("/users"); 
    } catch (e) {
        setError("Errore durante la cancellazione.");
    } finally {
        setLoading(false);
    }
  };

  const handleQuotaRenewal = async () => {
    setLoading(true);
    setIsQuotaConfirmationOpen(false);
    const today = new Date();
    let nextExpiryYear = today.getFullYear();
    const endOfYear = new Date(nextExpiryYear, 11, 31, 23, 59, 59);
    if (endOfYear < today) nextExpiryYear += 1;
    const newExpiryDate = new Date(nextExpiryYear, 11, 31, 23, 59, 59);
    const updateData = { quotaAssociativaScadenza: Timestamp.fromDate(newExpiryDate) };
    try {
        await updateDoc(doc(db, "users", userId), updateData);
        setUser(prev => ({ ...prev, ...updateData }));
    } catch (e) { setError("Errore rinnovo quota."); } 
    finally { setLoading(false); }
  };

  const handleConfirmRenewal = async () => {
    // MODIFICA A: Ora controlliamo endDate (non più calculatedEndDate)
    if (!renewalPeriod || !startDate || !endDate) {
        setError("Inserisci una data di fine valida.");
        return;
    }
    setLoading(true); 
    const newExpiryTimestamp = Timestamp.fromDate(new Date(endDate));
    
    const updateData = {
        tipoAbbonamento: renewalPeriod,
        abbonamentoScadenza: newExpiryTimestamp,
        ultimaModificaAbbonamento: Timestamp.fromDate(new Date()),
        costoAbbonamento: renewalPrice ? parseFloat(renewalPrice) : null,
        ingressiResidui: renewalPeriod === 'Carnet' ? parseInt(carnetEntries) : null
    };

    try {
        await updateDoc(doc(db, "users", userId), updateData);
        setUser(prev => ({ ...prev, ...updateData }));
        setIsRenewalFormOpen(false);
        setError(null);
    } catch (e) { setError("Errore rinnovo abbonamento."); } 
    finally { setLoading(false); }
  };

  if (loading) return (
    <MainContainer sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
       <CircularProgress sx={{ color: '#a32b2b' }} />
    </MainContainer>
  );

  if (error) return (
     <MainContainer><Alert severity="error">{error}</Alert></MainContainer>
  );

  const quotaScaduta = isExpired(user.quotaAssociativaScadenza);
  const abbonamentoScaduto = isExpired(user.abbonamentoScadenza);
  
  let certStatus = { label: 'Assente', color: 'default', icon: <ErrorOutlineIcon /> };
  if (latestCertificate) {
    const isCertExpired = isExpired(latestCertificate.expiresAt);
    if (latestCertificate.status === 'rejected') certStatus = { label: 'Rifiutato', color: 'error', icon: <ErrorOutlineIcon /> };
    else if (!latestCertificate.isValidated) certStatus = { label: 'In Revisione', color: 'warning', icon: <WarningAmberIcon /> };
    else if (isCertExpired) certStatus = { label: 'Scaduto', color: 'error', icon: <ErrorOutlineIcon /> };
    else certStatus = { label: 'Valido', color: 'success', icon: <CheckCircleIcon /> };
  }

  const quotaNextExpiryYear = new Date().getFullYear();
  let quotaPreviewExpiry = new Date(quotaNextExpiryYear, 11, 31);
  if (quotaPreviewExpiry < new Date()) quotaPreviewExpiry.setFullYear(quotaNextExpiryYear + 1);

  return (
    <MainContainer>
      {/* HEADER */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#fff', boxShadow: 1 }}>
            <ArrowBackIcon />
        </IconButton>
        <Box>
            <Typography variant="h4" fontWeight="800" sx={{ color: "#2d3436" }}>{user.fullName}</Typography>
            <Typography variant="body2" color="text.secondary">Gestione Profilo e Scadenze</Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* COLONNA SINISTRA */}
        <Grid item xs={12} md={4}>
            <ModernCard sx={{ textAlign: 'center' }}>
                <Avatar sx={{ width: 80, height: 80, margin: '0 auto 16px', bgcolor: '#a32b2b', fontSize: 32 }}>
                    {user.fullName.charAt(0)}
                </Avatar>
                <Typography variant="h6" fontWeight="bold">{user.fullName}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{user.email}</Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2} sx={{ textAlign: 'left' }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">TELEFONO</Typography>
                        <Typography variant="body1" fontWeight="500">{user.phone || "N/D"}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">REGOLAMENTO</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             {user.regolamentoAccettato ? <CheckCircleIcon color="success" fontSize="small"/> : <ErrorOutlineIcon color="error" fontSize="small"/>}
                             <Typography variant="body2">{user.regolamentoAccettato ? "Accettato" : "Non Accettato"}</Typography>
                        </Box>
                    </Box>
                </Stack>
            </ModernCard>

            {/* ZONA PERICOLOSA */}
            <ModernCard sx={{ border: '1px solid #ffcdd2', bgcolor: '#fffbfa' }}>
                <SectionTitle sx={{ color: '#d32f2f', fontSize: '0.9rem' }}>
                    <WarningAmberIcon fontSize="small" /> ZONA PERICOLOSA
                </SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Rimuovi utente e tutti i file dei certificati.
                </Typography>
                <Button fullWidth variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setIsDeleteDialogOpen(true)} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
                    Elimina Definitivamente
                </Button>
            </ModernCard>
        </Grid>

        {/* COLONNA DESTRA */}
        <Grid item xs={12} md={8}>
            {/* CERTIFICATO */}
            <ModernCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <SectionTitle><MedicalServicesIcon color="primary" /> Certificato Medico</SectionTitle>
                    <Chip icon={certStatus.icon} label={certStatus.label} color={certStatus.color} size="small" />
                </Box>
                <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                         <Typography variant="caption" color="text.secondary">SCADENZA</Typography>
                         <Typography variant="body1" fontWeight="bold">
                            {latestCertificate ? formatTimestamp(latestCertificate.expiresAt) : "Nessun dato"}
                         </Typography>
                    </Box>
                    {latestCertificate?.certificateUrl && (
                        <Button component={Link} href={latestCertificate.certificateUrl} target="_blank" size="small" variant="outlined">Vedi File</Button>
                    )}
                </Box>
            </ModernCard>

            {/* QUOTA ASSOCIATIVA */}
            <ModernCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <SectionTitle><CardMembershipIcon color="primary"/> Quota Associativa</SectionTitle>
                    <Chip label={quotaScaduta ? "SCADUTA" : "ATTIVA"} color={quotaScaduta ? "error" : "success"} size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">SCADE IL</Typography>
                        <Typography variant="h6" fontWeight="bold">{formatTimestamp(user.quotaAssociativaScadenza)}</Typography>
                    </Box>
                    {quotaScaduta && !isQuotaConfirmationOpen && (
                        <Button variant="contained" onClick={() => setIsQuotaConfirmationOpen(true)} sx={{ bgcolor: '#a32b2b' }}>Rinnova Quota</Button>
                    )}
                </Box>
                {isQuotaConfirmationOpen && (
                    <Alert severity="warning" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={handleQuotaRenewal}>CONFERMA</Button>}>
                        Rinnova fino al <strong>{formatTimestamp(Timestamp.fromDate(quotaPreviewExpiry))}</strong>?
                    </Alert>
                )}
            </ModernCard>

            {/* ABBONAMENTO E CARNET */}
            <ModernCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <SectionTitle><EventRepeatIcon color="primary"/> Stato Abbonamento</SectionTitle>
                    <Chip 
                      label={abbonamentoScaduto ? "SCADUTO" : user.tipoAbbonamento || "NESSUNO"} 
                      color={abbonamentoScaduto ? "error" : "primary"} 
                      variant="outlined" 
                      size="small" 
                    />
                </Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                     <Grid item xs={6}>
                        {user.tipoAbbonamento === 'Carnet' ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary">INGRESSI RESIDUI</Typography>
                            <Typography variant="h6" color={user.ingressiResidui > 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                                {user.ingressiResidui || 0} Ingressi
                            </Typography>
                            <Typography variant="caption" color="text.secondary">SCADENZA: {formatTimestamp(user.abbonamentoScadenza)}</Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="caption" color="text.secondary">SCADENZA ATTUALE</Typography>
                            <Typography variant="h6" color={abbonamentoScaduto ? 'error.main' : 'success.main'} fontWeight="bold">
                                {formatTimestamp(user.abbonamentoScadenza)}
                            </Typography>
                          </Box>
                        )}
                     </Grid>
                     <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Button variant={isRenewalFormOpen ? "outlined" : "contained"} onClick={() => setIsRenewalFormOpen(!isRenewalFormOpen)} sx={{ bgcolor: isRenewalFormOpen ? 'transparent' : '#a32b2b' }}>
                            {isRenewalFormOpen ? "Annulla" : "Nuovo Abbonamento / Carnet"}
                        </Button>
                     </Grid>
                </Grid>

                {isRenewalFormOpen && (
                    <Box sx={{ bgcolor: '#fff5f5', p: 3, borderRadius: 3, border: '1px dashed #a32b2b' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Frequenza / Tipo</InputLabel>
                                    <Select value={renewalPeriod} label="Frequenza / Tipo" onChange={(e) => setRenewalPeriod(e.target.value)}>
                                        <MenuItem value="Mensile">Mensile</MenuItem>
                                        <MenuItem value="Trimestrale">Trimestrale</MenuItem>
                                        <MenuItem value="Annuale">Annuale</MenuItem>
                                        <MenuItem value="Carnet">Carnet Ingressi</MenuItem>
                                        {/* MODIFICA A: Opzione aggiunta */}
                                        <MenuItem value="Personalizzato">Personalizzato / Manuale</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {renewalPeriod === 'Carnet' && (
                              <Grid item xs={12} sm={4}>
                                  <TextField 
                                    label="Numero Ingressi" 
                                    type="number" 
                                    size="small" 
                                    fullWidth 
                                    value={carnetEntries} 
                                    onChange={(e) => setCarnetEntries(e.target.value)} 
                                    sx={{ bgcolor: '#fff' }}
                                  />
                              </Grid>
                            )}

                            <Grid item xs={12} sm={renewalPeriod === 'Carnet' ? 4 : 4}>
                                <TextField label="Data Inizio" type="date" size="small" fullWidth value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                                {isOverlapping && renewalPeriod !== 'Carnet' && <Alert severity="warning" variant="outlined" sx={{ mt: 1, fontSize: '0.75rem', py: 0 }}>Coperta dal vecchio</Alert>}
                            </Grid>

                            {/* MODIFICA A: Rimosso il disabled. Se l'utente scrive a mano una data, sovrascrive il calcolo automatico! */}
                            {renewalPeriod !== 'Carnet' && (
                              <Grid item xs={12} sm={4}>
                                  <TextField 
                                    label="Scadenza" 
                                    type="date" 
                                    size="small" 
                                    fullWidth 
                                    value={endDate} 
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }} 
                                  />
                              </Grid>
                            )}

                            <Grid item xs={12} sm={6}>
                                <TextField label="Prezzo Totale (€)" type="number" size="small" fullWidth value={renewalPrice || ''} onChange={(e) => setRenewalPrice(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button fullWidth variant="contained" onClick={handleConfirmRenewal} sx={{ bgcolor: '#a32b2b', height: '40px' }}>Salva Rinnovo</Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </ModernCard>
        </Grid>
      </Grid>

      {/* DIALOGO CONFERMA ELIMINAZIONE */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>Eliminazione Totale Utente</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stai eliminando <strong>{user?.fullName}</strong>. <br/>
            Verranno rimossi: il profilo e tutti i file dei certificati. 
            Questa operazione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error">Elimina tutto definitivamente</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}